import { useState, useEffect } from "react";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { useCreateBrandMutation, useDeleteBrandMutation, useGetSurveyBrandsQuery, useUpdateBrandMutation, useUpdateSurveyMutation } from "src/redux/survey/survey.endpoints";
import { Brand, SurveyFull } from "src/redux/survey/survey.types";
import LogoUploader from "./logo-uploader";
import blackTrash from '../../../assets/icons/blackTrash.svg';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogOverlay, DialogTitle, DialogTrigger } from "../../../components/ui/dialog";
import { Button } from "../../../components/ui/button";
import { PixelInput } from '../../pixel-input/pixel-input';

const BrandSelector = ({ brand, setBrand, survey }: { brand: Brand | null; setBrand: (brand: Brand | null) => void; survey: SurveyFull }) => {
  const { data: allBrands, refetch: refetchBrands } = useGetSurveyBrandsQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [tempBrand, setTempBrand] = useState<Brand | null>(brand);
  const [brandOptions, setBrandOptions] = useState<{ id: string; name: string }[]>([]);
  const [updateBrand] = useUpdateBrandMutation();
  const [updateSurvey] = useUpdateSurveyMutation();
  const [createBrand] = useCreateBrandMutation();
  const [deleteBrand] = useDeleteBrandMutation();

  useEffect(() => {
    setTempBrand(brand);
  }, [brand]);

  useEffect(() => {
    if (Array.isArray(allBrands)) {
      const defaultBrand = allBrands.find(b => b.name.toLowerCase() === 'default');
      const otherBrands = allBrands.filter(b => b.name.toLowerCase() !== 'default')
        .sort((a, b) => a.name.localeCompare(b.name));
      setBrandOptions([
        ...(defaultBrand ? [defaultBrand] : []),
        { id: "new", name: "[+] Create a New Brand" },
        ...otherBrands,
      ]);
      const currentBrand = allBrands.find((b) => b.id === survey.brand?.id);
      if (currentBrand && JSON.stringify(currentBrand) !== JSON.stringify(brand)) {
        setBrand(currentBrand);
      }
    }
  }, [allBrands]);

  const defaultNewBrand: Partial<Brand> = {
    name: "New Brand",
    config: {
      bg_color: "#ffffff",
      font_color: "#000000",
      button_color: "#ffffff",
      font_secondary_color: "#000000",
      answer_border_color: "#000000",
      rtr_size: "300x250",
      footer_link: "https://invisibly.com",
      default_toast: {
        title: "Thanks for your feedback!",
        subtitle: "The survey is complete.",
        button_copy: "Learn more",
        button_url: "https://campaigninsights.co/",
        pixel: null,
        custom_tag: null,
      },
    },
    image_1: "",
    image_2: "",
    toast_image: "",
  } as Brand;

  const handleDropdownChange = async(selectedBrand: Brand | null) => {
    let brandToAssociate = selectedBrand;
    if (selectedBrand?.id === "new") {
      try {
        const newBrand = await createBrand(defaultNewBrand).unwrap();
        brandToAssociate = newBrand;
        setBrandOptions((prevOptions) => {
          const fixedOptions = prevOptions.filter((b) => b.name.toLowerCase() === "default" || b.id === "new");
          const sortedBrands = [...prevOptions.filter((b) => b.name.toLowerCase() !== "default" && b.id !== "new"), newBrand]
            .sort((a, b) => a.name.localeCompare(b.name));
          return [...fixedOptions, ...sortedBrands];
        });
      } catch (error) {
        console.error("Error creating brand:", error);
        return;
      }
    }
    try {
      await updateSurvey({
        ext_survey_id: survey.extSurveyId,
        title: survey.title,
        brand: brandToAssociate,
      });
      setBrand(brandToAssociate);
    } catch (error) {
      console.error("Failed to associate brand with survey", error);
    }
  };

  const handleEditBrand = async(updatedBrand: Brand | null) => {
    if (updatedBrand) {
      if (updatedBrand.name.trim() === "" ||
          updatedBrand.name.trim().toLowerCase() === 'default' ||
          !updatedBrand.config.default_toast?.title?.trim()) {
        setTempBrand(brand);
        return;
      }
      try {
        await updateBrand(updatedBrand).unwrap();
        setBrand(updatedBrand);
        setBrandOptions((prevOptions) => {
          const exists = prevOptions.some((b) => b.id === updatedBrand.id);
          if (exists) {
            return prevOptions.map((b) => (b.id === updatedBrand.id ? updatedBrand : b));
          }
          return [...prevOptions, updatedBrand];
        });
        await updateSurvey({
          ext_survey_id: survey.extSurveyId,
          title: survey.title,
          brand: updatedBrand,
        });
      } catch (error) {
        console.error("Error updating brand:", error);
      }
    } else {
      setBrand(null);
    }
  };

  const handleInputChange = (key: string, value: string | { key: string; value: string | null }, isConfig = false) => {
    if (!tempBrand) return;
    setTempBrand((prev) => {
      if (!prev) return prev;
      if (isConfig) {
        if (key === 'default_toast' && typeof value === 'object') {
          return {
            ...prev,
            config: {
              ...prev.config,
              default_toast: {
                ...prev.config.default_toast,
                [value.key]: value.value,
              },
            },
          };
        }
        return { ...prev, config: { ...prev.config, [key]: value } };
      }
      return { ...prev, [key]: value };
    });
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    if (!tempBrand) return;
    event.currentTarget.blur();
    handleEditBrand(tempBrand);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleBlur(e as unknown as React.FocusEvent<HTMLInputElement>);
    }
  };

  const handleDeleteClick = async(brand_id: string) => {
    const defaultBrand = allBrands?.find(b => b.name.toLowerCase() === 'default');
    try {
      await updateSurvey({
        ext_survey_id: survey.extSurveyId,
        title: survey.title,
        brand: defaultBrand || null,
      });
      await deleteBrand({ brand_id }).unwrap();
    } catch (error) {
      console.error("Error deleting brand:", error);
    }
    try {
      await refetchBrands();
    } catch (error) {
      console.error("Error refetching brands:", error);
    }
    setBrandOptions((prevOptions) => {
      const fixedOptions = prevOptions.filter((b) => b.name.toLowerCase() === "default" || b.id === "new");
      const remainingBrands = prevOptions.filter((b) => b.name.toLowerCase() !== "default" && b.id !== "new" && b.id !== brand_id)
        .sort((a, b) => a.name.localeCompare(b.name));
      return [...fixedOptions, ...remainingBrands];
    });
    setBrand(defaultBrand || null);
  };

  return (
    <div className="flex flex-col gap-4 p-4 bg-white shadow-md rounded-lg max-h-full overflow-y-auto">
      <h3 className="text-lg font-semibold">
        Brand Settings
      </h3>

      <div className="flex items-center gap-2">
        <Dropdown
          options={brandOptions}
          optionLabel="name"
          placeholder="Select a Brand"
          className="grow border border-[#d1d5db]"
          value={brandOptions.find((b) => b.id === brand?.id) || brandOptions[0]}
          onChange={(e) => {
            const selectedBrand = e.value.id === "default" ? null : e.value;
            handleDropdownChange(selectedBrand);
          }}
          itemTemplate={(option) => (
            <span
              style={{
                fontWeight: option.id === "new" || option.name.toLowerCase() === 'default'
                  ? "500"
                  : "normal",
              }}
            >
              {option.name}
            </span>
          )}
        />
        {brand && brand.name.toLowerCase() !== 'default' && brand.id !== "new" && (
          <Dialog>
            <DialogTrigger asChild>
              <button
                aria-label="Delete"
                className="rounded px-2 py-1  h-full text-black hover:bg-gray-201 cursor-pointer"
              >
                <img
                  src={blackTrash}
                  alt="Delete"
                  className="h-4"
                  draggable={false}
                />
              </button>
            </DialogTrigger>
            <DialogOverlay className="z-[1149]" />
            <DialogContent
              onClick={e => e.stopPropagation()}
              className="bg-white z-[1150]"
            >
              <DialogHeader>
                <DialogTitle>
                  Delete brand
                </DialogTitle>
                <DialogDescription>
                  Are you sure you want to delete this brand across
                  {' '}
                  <i>
                    all
                  </i>
                  {' '}
                  surveys?
                  <br />
                  This cannot be undone.
                </DialogDescription>
              </DialogHeader>
              <DialogFooter>
                <Button
                  type="submit"
                  onClick={() => handleDeleteClick(brand.id)}
                >
                  Delete
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {tempBrand && (
        <div className="flex flex-col">
          {[
            { label: "Brand Template Name", key: "name", value: tempBrand.name || "", required: true },
            { label: "Footer Link", key: "footer_link", value: tempBrand.config.footer_link || "", isConfig: true },
          ].map(({ label, key, value, isConfig, required }) => (
            <div
              key={key}
              className="flex flex-col mb-2"
            >
              <div className="text-xs font-medium text-gray-600">
                {label}
                {required && "*"}
              </div>
              <InputText
                placeholder={label}
                value={value}
                className={`p-inputtext-sm p-1 border border-[#d1d5db] ${tempBrand.name.toLowerCase() === 'default' ? 'opacity-50 cursor-not-allowed' : ''}`}
                onChange={(e) => handleInputChange(key, e.target.value, isConfig)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={tempBrand.name.toLowerCase() === 'default'}
              />
            </div>
          ))}

          <hr className="mt-2 mb-2" />

          {[
            { label: "Background Color", key: "bg_color", value: tempBrand.config.bg_color || "" },
            { label: "Title Color", key: "font_color", value: tempBrand.config.font_color || "" },
            { label: "Answer Button Color", key: "button_color", value: tempBrand.config.button_color || "" },
            { label: "Answer Text Color", key: "font_secondary_color", value: tempBrand.config.font_secondary_color || "" },
            { label: "Answer Border Color", key: "answer_border_color", value: tempBrand.config.answer_border_color || "" },
          ].map(({ label, key, value }) => (
            <span
              key={key}
              className="flex justify-between gap-2"
            >
              <div>
                {label + ":"}
              </div>
              <InputText
                type="color"
                value={value}
                onChange={(e) => tempBrand.name.toLowerCase() !== 'default' && handleInputChange(key, e.target.value, true)}
                onBlur={handleBlur}
              />
            </span>
          ))}

          <hr className="mt-2 mb-2" />

          {/* <RadioButtonGrid
            buttons={[{ label: '300x250', value: '300x250' }, { label: '970x250', value: '970x250' }]}
            extSurveyId={survey?.extSurveyId}
            initialValue={brand?.config.rtr_size}
            onClick={handleRTRSizeChange}
            title="Brand RTR Size"
            type={RadioButtonGridType.RtrSize}
            disabled={tempBrand.name.toLowerCase() === 'default'}
          /> */}

          <hr className="mt-4 mb-2" />

          <p className="font-semibold .radio-title mb-1">
            Brand Icon — RTR View
          </p>
          <LogoUploader
            src={brand?.image_2 || null}
            brand={brand}
            handleEditBrand={handleEditBrand}
            type="brand_2"
            disabled={tempBrand.name.toLowerCase() === 'default'}
          />

          <hr className="mt-4 mb-2" />

          <p className="font-semibold .radio-title mb-1">
            Brand Logo — Web View
          </p>
          <LogoUploader
            src={brand?.image_1 || null}
            brand={brand}
            handleEditBrand={handleEditBrand}
            type="brand_1"
            disabled={tempBrand.name.toLowerCase() === 'default'}
          />

          <hr className="mt-4 mb-3" />

          <p className="font-semibold .radio-title mb-1">
            Default Toast
          </p>
          {[
            { label: "Title", key: "title", value: tempBrand.config.default_toast?.title || "", required: true },
            { label: "Subtitle", key: "subtitle", value: tempBrand.config.default_toast?.subtitle || "" },
            { label: "Button Text", key: "button_copy", value: tempBrand.config.default_toast?.button_copy || "" },
            { label: "Button URL", key: "button_url", value: tempBrand.config.default_toast?.button_url || "" },
            { label: "Custom Tag", key: "custom_tag", value: tempBrand.config.default_toast?.custom_tag || "" },
          ].map(({ label, key, value, required }) => (
            <div
              key={key}
              className="flex flex-col mb-2"
            >
              <div className="text-xs font-medium text-gray-600">
                {label}
                {required && "*"}
              </div>
              <InputText
                placeholder={label}
                value={value}
                className={`p-inputtext-sm p-1 border border-[#d1d5db] ${tempBrand.name.toLowerCase() === 'default' ? 'opacity-50 cursor-not-allowed' : ''}`}
                onChange={(e) => handleInputChange('default_toast', { key, value: e.target.value }, true)}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                disabled={tempBrand.name.toLowerCase() === 'default'}
              />
            </div>
          ))}

          <div className="flex flex-col mb-2">
            <div className="text-xs font-medium text-gray-600">
              Pixel
            </div>
            <PixelInput
              initialPixel={tempBrand.config.default_toast?.pixel || ''}
              onSave={(value) => {
                const updatedBrand = {
                  ...tempBrand,
                  config: {
                    ...tempBrand.config,
                    default_toast: {
                      ...tempBrand.config.default_toast,
                      pixel: value,
                    },
                  },
                };
                setTempBrand(updatedBrand);
                handleEditBrand(updatedBrand);
              }}
              showToggle={false}
              disabled={tempBrand.name.toLowerCase() === 'default'}
            />
          </div>

          <p className="text-xs font-medium text-gray-600">
            Toast Image
          </p>
          <LogoUploader
            src={brand?.toast_image || null}
            brand={brand}
            handleEditBrand={handleEditBrand}
            type="default_toast"
            disabled={tempBrand.name.toLowerCase() === 'default'}
          />
        </div>
      )}
    </div>
  );
};

export default BrandSelector;